'use no memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo } from 'react';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import Icon, { IconSize } from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { TaskListTabs } from '../-components/TaskListTabs';
import { NoRequestTasksMessage } from './-components/NoRequestTasksMessage';
import { RequestTaskTab } from './-constants';
import { useRequestTasksTable } from './-hooks/useTable';
import { SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_individual/individual/_tasks/request-tasks/')({
  validateSearch: zodValidator(SearchSchema),
  component: RequestTasksPage
});
function RequestTasksPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.requestTasks'
  });
  useDocumentTitle(t('pageTitle'));
  const {
    table,
    displayState,
    totalCount,
    tabCounts,
    onRowClick
  } = useRequestTasksTable({
    emptyContent: <NoRequestTasksMessage />
  });
  const tabs = useMemo(() => [{
    value: RequestTaskTab.NOT_SUBMITTED,
    label: t('tabs.notSubmitted'),
    count: tabCounts[RequestTaskTab.NOT_SUBMITTED]
  }, {
    value: RequestTaskTab.PENDING_APPROVAL,
    label: t('tabs.pendingApproval'),
    count: tabCounts[RequestTaskTab.PENDING_APPROVAL]
  }, {
    value: RequestTaskTab.RETURNED,
    label: t('tabs.returned'),
    count: tabCounts[RequestTaskTab.RETURNED]
  }, {
    value: RequestTaskTab.APPROVED,
    label: t('tabs.approved'),
    count: tabCounts[RequestTaskTab.APPROVED]
  }, {
    value: RequestTaskTab.REJECTED,
    label: t('tabs.rejected'),
    count: tabCounts[RequestTaskTab.REJECTED]
  }, {
    value: RequestTaskTab.ALL,
    label: t('tabs.all'),
    count: tabCounts[RequestTaskTab.ALL]
  }], [t, tabCounts]);
  return <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-3 border-b border-neutral-muted-border bg-neutral-fill px-5 py-3 shadow-sm md:px-12 md:py-4">
        <span className="text-neutral-on-fill">
          <Icon iconName="FilePenLineIcon" size={IconSize.XL} inheritColor />
        </span>
        <Text as="h1" variant="h3">
          {t('pageTitle')}
        </Text>
      </div>
      <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
        <div className="overflow-clip rounded-b-[28px] border border-t-0 border-neutral-muted-border bg-neutral-fill/60 p-5 md:overflow-visible md:rounded-none md:border-0 md:bg-transparent md:p-0">
          <HStack className="items-center gap-2">
            <TaskListTabs value={search.taskTab} tabs={tabs} onChange={taskTab => navigate({
            search: prev => ({
              ...prev,
              taskTab,
              page: 1
            })
          })} />
          </HStack>
        </div>
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable table={table} displayState={displayState} isHoverable onRowClick={onRowClick} />
        </Card>
        <DataTablePagination table={table} totalCount={totalCount} pageOptions={[10, 20, 50, 100]} />
      </VStack>
    </div>;
}